import React, { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Mail, User, Clock, Send, CheckCircle, 
  AlertCircle, XCircle, ChevronLeft, Store, Search, 
  Bell, Filter, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const SupportCenter = () => {
  const [activeTab, setActiveTab] = useState('customer');
  const [conversations, setConversations] = useState([]);
  const [vendorConversations, setVendorConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({ support_conversations: 0, vendor_messages: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  
  // For sending new vendor message
  const [showNewVendorMessage, setShowNewVendorMessage] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState('');
  const [newVendorMessage, setNewVendorMessage] = useState('');

  useEffect(() => {
    loadData();
    loadUnreadCounts();
    
    // Poll for new messages
    const interval = setInterval(() => {
      loadUnreadCounts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadConversations();
  }, [activeTab, statusFilter]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  const loadData = async () => {
    try {
      const vendorsRes = await axiosInstance.get('/vendors/admin/all');
      setVendors(vendorsRes.data || []);
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadUnreadCounts = async () => {
    try {
      const response = await axiosInstance.get('/support/admin/unread-counts');
      setUnreadCounts(response.data);
    } catch (error) {
      console.error('Error loading unread counts:', error);
    }
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      if (activeTab === 'customer') {
        const params = statusFilter ? `?status=${statusFilter}` : '';
        const response = await axiosInstance.get(`/support/admin/conversations${params}`);
        setConversations(response.data.conversations || []);
      } else {
        const response = await axiosInstance.get('/support/admin/vendor-conversations');
        setVendorConversations(response.data.conversations || []);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Fehler beim Laden der Nachrichten');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (conversationId, type = 'customer') => {
    try {
      if (type === 'customer') {
        const response = await axiosInstance.get(`/support/conversations/${conversationId}`);
        setSelectedConversation({ ...response.data, type: 'customer' });
      } else {
        // For vendor conversations, we already have the data
        const conv = vendorConversations.find(c => c.id === conversationId);
        if (conv) {
          setSelectedConversation({ ...conv, type: 'vendor' });
        }
      }
      loadUnreadCounts();
    } catch (error) {
      toast.error('Fehler beim Laden');
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      if (selectedConversation.type === 'customer') {
        await axiosInstance.post(`/support/conversations/${selectedConversation.id}/reply`, {
          message: replyMessage.trim()
        });
      } else {
        await axiosInstance.post(`/support/vendor-messages/${selectedConversation.vendor_id}`, {
          message: replyMessage.trim()
        });
      }
      
      setReplyMessage('');
      await loadConversation(selectedConversation.id, selectedConversation.type);
      loadConversations();
      toast.success('Nachricht gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedConversation || selectedConversation.type !== 'customer') return;
    
    try {
      await axiosInstance.put(`/support/admin/conversations/${selectedConversation.id}/status?status=${status}`);
      setSelectedConversation({ ...selectedConversation, status });
      loadConversations();
      toast.success(`Status auf "${getStatusLabel(status)}" geändert`);
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const handleSendNewVendorMessage = async () => {
    if (!selectedVendor || !newVendorMessage.trim()) {
      toast.error('Bitte wählen Sie einen Verkäufer und geben Sie eine Nachricht ein');
      return;
    }
    
    setSending(true);
    try {
      await axiosInstance.post(`/support/vendor-messages/${selectedVendor}`, {
        message: newVendorMessage.trim()
      });
      
      setNewVendorMessage('');
      setSelectedVendor('');
      setShowNewVendorMessage(false);
      loadConversations();
      toast.success('Nachricht an Verkäufer gesendet');
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'open': return 'Offen';
      case 'pending': return 'Wartet auf Antwort';
      case 'resolved': return 'Gelöst';
      case 'closed': return 'Geschlossen';
      default: return status;
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'general': return 'Allgemein';
      case 'order': return 'Bestellung';
      case 'technical': return 'Technisch';
      case 'vendor_support': return 'Verkäufer';
      default: return category;
    }
  };

  const filteredConversations = (activeTab === 'customer' ? conversations : vendorConversations)
    .filter(conv => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        conv.subject?.toLowerCase().includes(query) ||
        conv.user_email?.toLowerCase().includes(query) ||
        conv.user_name?.toLowerCase().includes(query) ||
        conv.vendor_name?.toLowerCase().includes(query) ||
        conv.vendor_email?.toLowerCase().includes(query)
      );
    });

  return (
    <div className="min-h-screen py-4 px-3 sm:px-4" style={{ backgroundColor: '#F5EDE4' }} data-testid="support-center">
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader
          title="Support-Center"
          subtitle="Kunden- und Verkäufer-Nachrichten verwalten"
          icon={MessageSquare}
        />

        {/* Section: Nachrichtentyp - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0F8FF]">
            <Mail className="h-4 w-4 text-[#60A0D0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Nachrichtentyp wählen</h2>
        </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedConversation(null); }}>
        <TabsList className="mb-4 bg-[#C4B5A4] p-1 rounded-none border-0 gap-1 inline-flex">
          <TabsTrigger value="customer" className="relative rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">
            <User className="h-4 w-4 mr-2" />
            Kundenanfragen
            {unreadCounts.support_conversations > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs">
                {unreadCounts.support_conversations}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="vendor" className="relative rounded-none border-0 bg-transparent data-[state=active]:bg-[#E8DFD4] data-[state=active]:text-[#5D4037] data-[state=active]:font-medium text-[#5D4037]/70 px-4 py-2">
            <Store className="h-4 w-4 mr-2" />
            Verkäufer-Nachrichten
            {unreadCounts.vendor_messages > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs">
                {unreadCounts.vendor_messages}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversation List */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {activeTab === 'customer' ? 'Anfragen' : 'Verkäufer'}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={loadConversations}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              {/* Search */}
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Suchen..."
                  className="pl-9"
                />
              </div>
              
              {/* Filter (only for customer tab) */}
              {activeTab === 'customer' && (
                <div className="flex gap-1 mt-2 flex-wrap">
                  <Button
                    variant={statusFilter === '' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('')}
                    className="text-xs"
                  >
                    Alle
                  </Button>
                  <Button
                    variant={statusFilter === 'open' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('open')}
                    className="text-xs"
                  >
                    Offen
                  </Button>
                  <Button
                    variant={statusFilter === 'pending' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setStatusFilter('pending')}
                    className="text-xs"
                  >
                    Wartend
                  </Button>
                </div>
              )}
              
              {/* New vendor message button */}
              {activeTab === 'vendor' && (
                <Button
                  onClick={() => setShowNewVendorMessage(true)}
                  className="w-full mt-2"
                  size="sm"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Verkäufer kontaktieren
                </Button>
              )}
            </CardHeader>
            
            <CardContent className="max-h-[500px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-gray-500">Lädt...</div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-20" />
                  <p>Keine Nachrichten</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredConversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => loadConversation(conv.id, activeTab)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedConversation?.id === conv.id
                          ? 'bg-blue-50 border-blue-300'
                          : conv.unread_by_admin
                          ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {activeTab === 'customer' ? conv.subject : conv.vendor_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {activeTab === 'customer' 
                              ? `${conv.user_name} - ${conv.user_email || 'Gast'}`
                              : conv.vendor_email
                            }
                          </p>
                        </div>
                        {activeTab === 'customer' && (
                          <Badge className={`text-xs shrink-0 ${getStatusColor(conv.status)}`}>
                            {getStatusLabel(conv.status)}
                          </Badge>
                        )}
                        {conv.unread_by_admin && (
                          <span className="w-2 h-2 bg-orange-500 rounded-full shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTime(conv.updated_at || conv.last_message_at)}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Conversation Detail */}
          <Card className="lg:col-span-2">
            {selectedConversation ? (
              <>
                <CardHeader className="border-b">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {selectedConversation.type === 'customer' 
                          ? selectedConversation.subject 
                          : `Nachricht an ${selectedConversation.vendor_name}`
                        }
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-1">
                        {selectedConversation.type === 'customer' ? (
                          <>
                            <span className="font-medium">{selectedConversation.user_name}</span>
                            {selectedConversation.user_email && ` - ${selectedConversation.user_email}`}
                            {selectedConversation.category && (
                              <Badge className="ml-2 text-xs" variant="outline">
                                {getCategoryLabel(selectedConversation.category)}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span>{selectedConversation.vendor_email}</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Status Actions for Customer Conversations */}
                    {selectedConversation.type === 'customer' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus('resolved')}
                          className="text-green-600"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Gelöst
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleUpdateStatus('closed')}
                          className="text-gray-600"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          Schließen
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                
                <CardContent className="p-0">
                  {/* Messages */}
                  <div className="h-[350px] overflow-y-auto p-4 space-y-3">
                    {selectedConversation.messages?.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${
                          msg.sender === 'admin' ? 'justify-end' : 'justify-start'
                        }`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            msg.sender === 'admin'
                              ? 'bg-blue-500 text-white rounded-br-none'
                              : 'bg-gray-100 text-gray-800 rounded-bl-none'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          <p className={`text-xs mt-1 ${
                            msg.sender === 'admin' ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            {msg.sender_name && `${msg.sender_name} - `}
                            {formatTime(msg.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Reply Input */}
                  <div className="p-4 border-t bg-gray-50">
                    <div className="flex gap-2">
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Ihre Antwort schreiben..."
                        className="min-h-[80px] resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.ctrlKey) {
                            handleSendReply();
                          }
                        }}
                      />
                      <Button
                        onClick={handleSendReply}
                        disabled={sending || !replyMessage.trim()}
                        className="self-end"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Strg+Enter zum Senden
                    </p>
                  </div>
                </CardContent>
              </>
            ) : (
              <CardContent className="flex items-center justify-center h-[500px] text-gray-500">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>Wählen Sie eine Konversation aus</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {/* New Vendor Message Modal */}
        {showNewVendorMessage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Verkäufer kontaktieren</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Verkäufer auswählen</label>
                  <select
                    value={selectedVendor}
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    className="w-full mt-1 border rounded-lg px-3 py-2"
                  >
                    <option value="">Verkäufer wählen...</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.vendor_profile?.shop_name || vendor.first_name} - {vendor.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Nachricht</label>
                  <Textarea
                    value={newVendorMessage}
                    onChange={(e) => setNewVendorMessage(e.target.value)}
                    placeholder="Ihre Nachricht an den Verkäufer..."
                    className="mt-1 min-h-[120px]"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowNewVendorMessage(false)}>
                    Abbrechen
                  </Button>
                  <Button onClick={handleSendNewVendorMessage} disabled={sending}>
                    {sending ? 'Wird gesendet...' : 'Senden'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </Tabs>
      </div>
    </div>
  );
};

export default SupportCenter;
